import { useTooltipActions } from '../../../../core/components/overlay-tooltip/useTooltipActions.js';
import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { getZoomStep } from '../../../../core/components/zoom/utils/get-zoom-step.js';
import type { AnyNumberScale } from '../../../../core/types/scales.js';
import type { ZoomChangeType } from '../../../../core/types/zoom.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { reduceZoomDomain } from '../../../store/reducer-zoom.js';
import { useSetState } from '../../../store/selectors.js';
import type { XYChartState } from '../../../types/state.js';
import {
  isInfiniteZoomEnabledFor,
  isZoomEnabledFor,
} from '../../../utils/axis-interactions.js';
import { getCurrentScale, getInitialScale } from '../../../utils/get-scale.js';
import { handleZoomChange } from '../utils/zoom-change-handler.js';

/**
 * Returns a callback which given a delta in pixels, will apply the zoom interaction and update the state accordingly
 */
export const useApplyZoomDelta = () => {
  const setState = useSetState();
  const axisIdToScales = useXYChartScales();
  const { axes } = useXYChartData();
  const { resetSelectionState } = useSynchronizedSelection();
  const { reset } = useTooltipActions();

  return (deltaStart: number, deltaEnd: number, type: ZoomChangeType) => {
    const newCurrentDomain: XYChartState['currentDomain'] = new Map();

    let zoomInEnabled = false;
    let zoomOutEnabled = false;

    const allAxesHaveInfiniteZoom = axes
      .filter(isZoomEnabledFor)
      .every((axis) => isInfiniteZoomEnabledFor(axis));

    for (const axis of axes) {
      if (isZoomEnabledFor(axis)) {
        const currentScale = getCurrentScale(axisIdToScales, axis.id);
        const initialScale = getInitialScale(axisIdToScales, axis.id);

        const initialDomain = initialScale.domain();

        const {
          nextDomain,
          zoomInEnabled: inEnabled,
          zoomOutEnabled: outEnabled,
        } = getZoomStep(
          currentScale as AnyNumberScale,
          [+initialDomain[0], +initialDomain[1]],
          allAxesHaveInfiniteZoom,
          deltaStart,
          deltaEnd,
        );

        zoomInEnabled ||= inEnabled;
        zoomOutEnabled ||= outEnabled;

        newCurrentDomain.set(
          axis.id,
          nextDomain
            ? { min: nextDomain[0].valueOf(), max: nextDomain[1].valueOf() }
            : undefined,
        );

        handleZoomChange(axis, nextDomain[0], nextDomain[1], type);
      }
    }
    resetSelectionState?.();
    setState(reduceZoomDomain(newCurrentDomain, zoomInEnabled, zoomOutEnabled));
    reset();
  };
};
